<template>
  <div class="pos-header">
    <div class="pos-header-content">
      <!-- User Profile Section -->
      <div class="pos-user-section">
        <div class="pos-user-card">
          <div class="pos-user-avatar">
            <img
              src="@/assets/images/user.png"
              alt="user"
              class="user-avatar"
            />
          </div>
          <div class="pos-user-info">
            <h3 class="user-name">{{ userName }}</h3>
            <div class="outlet-info">
              <h4 class="outlet-name">{{ outletName }}</h4>
              <p class="outlet-address">{{ outletAddress }}</p>
            </div>
            <div class="shift-time" v-if="isOpenShift">
              <i class="pi pi-clock"></i>
              <span
                >{{ currentDate | shortDate }} | {{ hours }}:{{ minutes }}:{{
                  seconds
                }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Cart Summary Section -->
      <div class="pos-cart-summary" v-if="isOpenShift">
        <div class="cart-summary-card">
          <div class="cart-summary-item">
            <i class="pi pi-shopping-cart"></i>
            <div class="cart-summary-content">
              <span class="cart-summary-label">Total Qty</span>
              <span class="cart-summary-value">{{ qtyTotal }}</span>
            </div>
          </div>
          <div class="cart-summary-item">
            <i class="pi pi-tag"></i>
            <div class="cart-summary-content">
              <span class="cart-summary-label">Jenis Item</span>
              <span class="cart-summary-value">{{ cartLength }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Shift Controls Section -->
      <div class="pos-shift-section">
        <div class="shift-controls">
          <div v-if="isPendingShift" class="shift-pending">
            <Button
              @click="$emit('open-shift')"
              icon="pi pi-check-circle"
              label="Mulai Shift"
              class="p-button-primary"
            />
          </div>
          <div v-else-if="!isOpenShift" class="shift-closed">
            <div class="shift-status">
              <i class="pi pi-lock" style="color: #ef4444"></i>
              <span>Shift Tertutup</span>
            </div>
          </div>
          <div v-else class="shift-open">
            <div class="shift-status">
              <i class="pi pi-check-circle" style="color: #10b981"></i>
              <span>Shift Terbuka</span>
            </div>
            <!-- Audit Date Display -->
            <div class="shift-audit-date" v-if="auditDate">
              <i class="pi pi-calendar"></i>
              <span>Audit: {{ auditDate | date }}</span>
            </div>
          </div>

          <!-- Fullscreen Toggle Button -->
          <Button
            :icon="
              isFullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'
            "
            class="p-button-rounded p-button-text fullscreen-btn"
            @click="toggleFullscreen"
            :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
          />
        </div>
      </div>
    </div>

    <!-- Dialogs remain unchanged -->
    <Dialog
      :visible.sync="showShiftWarningProp"
      :style="{ width: '450px' }"
      header="Warning"
      :modal="true"
      @hide="$emit('close-shift-warning')"
    >
      <div class="confirmation-content">
        <span>
          <b>Anda belum memiliki shift</b>
        </span>
      </div>
    </Dialog>

    <Dialog
      :visible.sync="showOpenShiftDialog"
      :style="{ width: '400px' }"
      header="Mulai Shift"
      :modal="true"
    >
      <div class="p-grid p-col-12">
        <div class="p-col-4">
          <b>Uang Awal:</b>
        </div>
        <div class="p-col-8 p-text-right" style="font-size: 26px">
          <b>{{ openingBalance | currency }}</b>
        </div>
      </div>

      <template #footer>
        <Button
          label="Mulai"
          class="p-button-outlined"
          @click="handleConfirmOpenShift"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="showAuditDateWarning"
      :style="{ width: '500px' }"
      header="WARNING"
      :modal="true"
      :closable="false"
    >
      <div class="p-grid p-col-12 text-center">
        <b style="font-size: 18px"
          >Audit date tidak sesuai, silahkan tutup shift hari ini</b
        ><br />
        <b style="font-size: 18px">dan buka shift kembali</b>
      </div>

      <template #footer>
        <Button
          label="OK"
          class="p-button-text"
          @click="$emit('close-audit-warning')"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
export default {
  name: 'POSHeader',
  data() {
    return {
      isFullscreen: false
    }
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    outlet: {
      type: Object,
      default: () => ({})
    },
    isOpenShift: {
      type: Boolean,
      default: false
    },
    isPendingShift: {
      type: Boolean,
      default: false
    },
    openingBalance: {
      type: Number,
      default: 0
    },
    showOpenShiftDialogProp: {
      type: Boolean,
      default: false
    },
    showShiftWarningProp: {
      type: Boolean,
      default: false
    },
    qtyTotal: {
      type: Number,
      default: 0
    },
    cartLength: {
      type: Number,
      default: 0
    },
    hours: {
      type: String,
      default: '00'
    },
    minutes: {
      type: String,
      default: '00'
    },
    seconds: {
      type: String,
      default: '00'
    }
  },
  computed: {
    userName() {
      return this.user && this.user.name ? this.user.name : ''
    },
    outletName() {
      // Use outlet prop if available
      if (this.outlet && this.outlet.name) {
        return this.outlet.name
      }
      if (this.outlet && this.outlet.outlet_name) {
        return this.outlet.outlet_name
      }
      // Fallback to user data
      if (this.user && this.user.outlet && this.user.outlet.name) {
        return this.user.outlet.name
      }
      if (this.user && this.user.outlet_name) {
        return this.user.outlet_name
      }
      // Final fallback
      return process.env.VUE_APP_STORE_NAME || 'MPOS STORE'
    },
    outletAddress() {
      // Use outlet prop if available
      if (this.outlet && this.outlet.address) {
        return this.outlet.address
      }
      if (this.outlet && this.outlet.outlet_address) {
        return this.outlet.outlet_address
      }
      // Fallback to user data
      if (this.user && this.user.outlet && this.user.outlet.address) {
        return this.user.outlet.address
      }
      if (this.user && this.user.outlet_address) {
        return this.user.outlet_address
      }
      // Final fallback
      return process.env.VUE_APP_STORE_ADDR || ''
    },
    openedAt() {
      return this.user && this.user.shift && this.user.shift.opened_at
        ? this.user.shift.opened_at
        : null
    },
    auditDate() {
      return this.user && this.user.shift && this.user.shift.audit_date
        ? this.user.shift.audit_date
        : null
    },
    currentDate() {
      return new Date()
    },
    showOpenShiftDialog() {
      return this.showOpenShiftDialogProp && this.openingBalance > 0
    },
    showAuditDateWarning() {
      return this.user && this.user.shift && this.user.shift.audit_date_warning
        ? this.user.shift.audit_date_warning
        : false
    }
  },

  methods: {
    handleConfirmOpenShift() {
      this.$emit('confirm-open-shift')
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen()
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen()
        } else if (document.documentElement.msRequestFullscreen) {
          document.documentElement.msRequestFullscreen()
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    checkAuditDate() {
      if (this.user && this.user.shift && this.user.shift.audit_date) {
        const today = new Date()
        const auditDate = new Date(this.user.shift.audit_date)

        if (today.getDate() !== auditDate.getDate() && today.getHours() >= 8) {
          this.$emit('audit-date-warning')
        }
      }
    },
    setupFullscreenListeners() {
      this.handleFullscreenChange = () => {
        this.isFullscreen = !!document.fullscreenElement
      }

      this.handleKeydown = event => {
        if (event.key === 'F11') {
          event.preventDefault()
          this.toggleFullscreen()
        }
      }

      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener(
        'webkitfullscreenchange',
        this.handleFullscreenChange
      )
      document.addEventListener(
        'mozfullscreenchange',
        this.handleFullscreenChange
      )
      document.addEventListener(
        'MSFullscreenChange',
        this.handleFullscreenChange
      )
      document.addEventListener('keydown', this.handleKeydown)
    },
    removeFullscreenListeners() {
      if (this.handleFullscreenChange) {
        document.removeEventListener(
          'fullscreenchange',
          this.handleFullscreenChange
        )
        document.removeEventListener(
          'webkitfullscreenchange',
          this.handleFullscreenChange
        )
        document.removeEventListener(
          'mozfullscreenchange',
          this.handleFullscreenChange
        )
        document.removeEventListener(
          'MSFullscreenChange',
          this.handleFullscreenChange
        )
      }
      if (this.handleKeydown) {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  mounted() {
    this.checkAuditDate()
    this.setupFullscreenListeners()
  },
  beforeDestroy() {
    this.removeFullscreenListeners()
  }
}
</script>

<style scoped lang="scss">
.pos-header {
  height: 100%;
  position: relative;
}

.pos-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
}

/* User Section */
.pos-user-section {
  flex: 1;
}

.pos-user-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pos-user-avatar {
  flex-shrink: 0;
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.pos-user-info {
  flex: 1;
}

.user-name {
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.outlet-info {
  margin: 0 0 0.5rem 0;
}

.outlet-name {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.outlet-address {
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

.shift-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #059669;
  font-weight: 500;

  i {
    font-size: 1rem;
  }
}

/* Cart Summary Section */
.pos-cart-summary {
  flex-shrink: 0;
}

.cart-summary-card {
  display: flex;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.cart-summary-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  i {
    font-size: 1.25rem;
    color: #3b82f6;
  }
}

.cart-summary-content {
  display: flex;
  flex-direction: column;
}

.cart-summary-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.cart-summary-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
}

/* Shift Section */
.pos-shift-section {
  flex-shrink: 0;
}

.shift-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.shift-pending {
  .p-button {
    min-width: 120px;
  }
}

.shift-closed,
.shift-open {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid;
}

.shift-closed {
  background: #fef2f2;
  border-color: #fecaca;

  .shift-status {
    color: #dc2626;
  }
}

.shift-open {
  background: #f0fdf4;
  border-color: #bbf7d0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .shift-status {
    color: #059669;
  }
}

.shift-audit-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  padding-top: 0.5rem;
  border-top: 1px solid #d1d5db;
  margin-top: 0.25rem;

  i {
    font-size: 0.875rem;
    color: #059669;
  }
}

.shift-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;

  i {
    font-size: 1rem;
  }
}

.fullscreen-btn {
  width: 2.5rem !important;
  height: 2.5rem !important;
  background: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  color: #059669 !important;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    background: #dcfce7 !important;
    border-color: #86efac !important;
    transform: scale(1.05);
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2) !important;
  }

  .p-button-icon {
    font-size: 1rem !important;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .pos-header-content {
    gap: 1rem;
  }

  .cart-summary-card {
    gap: 1rem;
    padding: 0.75rem;
  }

  .user-avatar {
    width: 50px;
    height: 50px;
  }

  .user-name {
    font-size: 1.125rem;
  }
}

@media (max-width: 768px) {
  .pos-header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .pos-user-card {
    justify-content: center;
  }

  .cart-summary-card {
    justify-content: center;
  }

  .pos-shift-section {
    display: flex;
    justify-content: center;
  }
}
</style>
